import React, { useState, useRef, useEffect } from 'react'
import MuiLink from '@material-ui/core/Link'
import PropTypes from 'prop-types'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import IconButton from '@material-ui/core/IconButton'
import Chip from '@material-ui/core/Chip'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Tooltip from '@material-ui/core/Tooltip'
import {
  Calendar,
  Clock,
  MapPin,
  COACH_PROFILE_DIALOG_META_ICON_PROPS,
} from '@lib/pga-coach/coach-profile-icons'
import CloseIcon from '@material-ui/icons/Close'
import FavoriteIcon from '@material-ui/icons/Favorite'
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder'
import ButtonCta from '@components/atoms/button-cta'
import defaults, { metersPerMile } from '@config/defaults'
import pluralize from '@lib/pluralize'
import { makeStyles } from '@material-ui/core/styles'
import useIsMobile from '@hooks/useIsMobile'
import { PGA_COACH_PRIVATE_LISTING_IMAGE } from '@lib/pga-coach/constants'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import MarkdownText from './markdown-text'

const useStyles = makeStyles((theme) => ({
  modalPaper: {
    borderRadius: 12,
    maxWidth: 680,
    '&.MuiDialog-paperFullScreen': {
      borderRadius: 0,
    },
  },
  modalContent: {
    padding: 0,
    paddingTop: '0 !important',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalScrollArea: {
    flex: 1,
    overflowY: 'auto',
    // Sticky header actions use negative margin to sit over the hero without shifting layout math.
    position: 'relative',
  },
  /** Pinned to the top of the dialog scrollport so favorite + close stay visible while scrolling. */
  modalHeaderStickyActions: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
    // Pull hero up under this row (padding 8+8 + ~32px icon buttons ≈ spacing(6)).
    marginBottom: theme.spacing(-6),
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'auto',
    },
  },
  modalHeaderImageWrap: {
    position: 'relative',
    width: '100%',
    height: 300,
    backgroundColor: '#e7eef6',
    [theme.breakpoints.down('xs')]: {
      height: 220,
    },
  },
  modalHeaderImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  modalHeaderActionButton: {
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 8px rgba(15, 34, 52, 0.18)',
    padding: theme.spacing(0.75),
    '&:hover': {
      backgroundColor: '#f5f7fa',
    },
  },
  modalFavoriteIconSaved: {
    color: theme.palette.blue.main,
  },
  modalBody: {
    padding: theme.spacing(2.5, 3, 2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  /** Tag row above the title (matches list card footer chip look). */
  modalTopChips: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: 0,
    marginBottom: theme.spacing(1.25),
    minWidth: 0,
  },
  modalTitle: {
    fontWeight: 800,
    lineHeight: 1.15,
    marginBottom: theme.spacing(1.25),
  },
  modalDescription: {
    color: '#5f6771',
    fontSize: '1.06rem',
    lineHeight: 1.45,
    whiteSpace: 'pre-line',
    marginBottom: theme.spacing(0.5),
    '& .MuiTypography-root': {
      color: 'inherit',
      fontSize: 'inherit',
      lineHeight: 'inherit',
    },
  },
  modalDescriptionClamped: {
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-line-clamp': 4,
    '-webkit-box-orient': 'vertical',
  },
  modalDescriptionWrap: {
    marginBottom: theme.spacing(2),
  },
  modalCoachRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    backgroundColor: '#f0f2f5',
    marginBottom: theme.spacing(2),
  },
  modalCoachAvatar: {
    width: 56,
    height: 56,
    flexShrink: 0,
  },
  modalCoachInfo: {
    flex: 1,
    minWidth: 0,
  },
  modalCoachName: {
    fontWeight: 700,
    marginBottom: 2,
  },
  modalCoachNameLink: {
    fontWeight: 700,
    marginBottom: 2,
    display: 'block',
    textDecoration: 'none',
    color: theme.palette.blue.main,
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.blue.main,
    },
  },
  modalCoachText: {
    color: '#596270',
    lineHeight: 1.35,
    whiteSpace: 'pre-line',
    '& .MuiTypography-root': {
      color: 'inherit',
      lineHeight: 1.35,
    },
  },
  modalCoachTextClamped: {
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
  },
  modalCoachToggle: {
    background: 'none',
    border: 'none',
    padding: theme.spacing(0.5, 0.75),
    marginTop: theme.spacing(0.25),
    marginLeft: theme.spacing(-0.75),
    cursor: 'pointer',
    color: theme.palette.blue.main,
    fontSize: '0.8rem',
    fontWeight: 600,
    fontFamily: 'inherit',
    lineHeight: 1.35,
    display: 'inline-block',
    verticalAlign: 'baseline',
    textAlign: 'left',
  },
  modalMetaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(2),
  },
  modalMetaIcon: {
    flexShrink: 0,
    fontSize: '1.375rem',
    color: theme.palette.text.secondary,
  },
  modalMetaTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  modalMetaTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: 1.25,
    color: '#2a3240',
  },
  modalMetaSub: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.35,
    color: '#687381',
    marginTop: theme.spacing(0.25),
  },
  /** Matches `private-offering.jsx` footer `chip` (list card tags). */
  modalChip: {
    '&.MuiChip-root': {
      borderRadius: 999,
    },
    height: 'auto',
    minHeight: 24,
    border: 'none',
    backgroundColor: theme.palette.blue.light25,
    color: '#8a94a3',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(11),
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingTop: theme.spacing(0.25),
      paddingBottom: theme.spacing(0.25),
      lineHeight: 1.2,
    },
  },
  modalFooter: {
    borderTop: '1px solid #dfe3ea',
    padding: theme.spacing(1.75, 3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexShrink: 0,
    backgroundColor: '#fff',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 2),
    },
  },
  modalFooterPriceArea: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.25),
  },
  modalPrice: {
    fontWeight: 800,
    lineHeight: 1.15,
    marginBottom: 0,
  },
  modalPriceSuffix: {
    color: '#687381',
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.15,
  },
  modalBookButton: {
    '&.MuiButton-root': {
      borderRadius: 999,
    },
    minHeight: 32,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    lineHeight: 1.2,
    padding: theme.spacing(0.75, 2.25),
    whiteSpace: 'nowrap',
    boxShadow: 'none',
  },
}))

const formatDistance = (geoDistanceMeters) => {
  if (geoDistanceMeters == null) return null
  const miles = Number(geoDistanceMeters) / metersPerMile
  if (Number.isNaN(miles)) return null
  return `${miles.toFixed(1)} mi`
}

/** Middle-dot separated secondary detail line (journey modal mock). */
const joinOfferingMetaDetails = (parts) => parts.filter(Boolean).join(' · ')

const formatDuration = (durationInMinutes) => {
  if (!durationInMinutes || Number.isNaN(Number(durationInMinutes))) return null
  const value = Number(durationInMinutes)
  if (value < 60) return `${value} min`
  if (value % 60 === 0) {
    const hours = value / 60
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  }
  const hours = Math.floor(value / 60)
  const minutes = value % 60
  return `${hours}h ${minutes}m`
}

/** Clear lesson-length copy for modal (avoids sounding like “available within 30 min”). */
const formatLessonDurationLabel = (durationInMinutes) => {
  if (durationInMinutes == null || Number.isNaN(Number(durationInMinutes))) return null
  const m = Number(durationInMinutes)
  if (m <= 0) return null
  if (m < 60) {
    return `Lesson duration: ${m} ${pluralize(m, 'minute', 's')}`
  }
  const hours = Math.floor(m / 60)
  const minutes = m % 60
  const hourPart = `${hours} ${pluralize(hours, 'hour', 's')}`
  if (!minutes) {
    return `Lesson duration: ${hourPart}`
  }
  const minPart = `${minutes} ${pluralize(minutes, 'minute', 's')}`
  return `Lesson duration: ${hourPart} ${minPart}`
}

const toTitleFormat = (value = '') => value
  .split(/[\s_-]+/)
  .filter(Boolean)
  .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
  .join(' ')

function PrivateOfferingDialog({
  open,
  onClose,
  offering,
  formatPrice,
  formatLocation,
  getTagLabels,
  onPrimaryActionClick,
  availabilityWindowLabel,
  isOfferingFavorite,
  onToggleOfferingFavorite,
}) {
  const classes = useStyles()
  const isMobile = useIsMobile()
  const [bioExpanded, setBioExpanded] = useState(false)
  const [bioOverflows, setBioOverflows] = useState(false)
  const bioRef = useRef(null)
  const [descExpanded, setDescExpanded] = useState(false)
  const [descOverflows, setDescOverflows] = useState(false)
  const descRef = useRef(null)

  useEffect(() => {
    if (!open) {
      setBioExpanded(false)
      setBioOverflows(false)
      return undefined
    }
    const frame = requestAnimationFrame(() => {
      if (bioRef.current) {
        setBioOverflows(bioRef.current.scrollHeight > bioRef.current.clientHeight)
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [open, offering.coach.bio])

  useEffect(() => {
    if (!open) {
      setDescExpanded(false)
      setDescOverflows(false)
      return undefined
    }
    const frame = requestAnimationFrame(() => {
      if (descRef.current) {
        setDescOverflows(descRef.current.scrollHeight > descRef.current.clientHeight)
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [open, offering.description])

  const {
    ctaLabel,
    ctaUrl,
    geoDistanceMeters,
    coachFacilityFullAddress,
    durationInMinutes,
    nextAvailableAt,
    priceLabel,
    venueName,
  } = offering
  const distanceLabel = formatDistance(geoDistanceMeters)
  const durationLabel = formatDuration(durationInMinutes)
  const nextAvailableAtDate = nextAvailableAt ? new Date(nextAvailableAt) : null
  const scheduleDatePrimary = nextAvailableAtDate && !Number.isNaN(nextAvailableAtDate.getTime())
    ? nextAvailableAtDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    })
    : null
  const scheduleTimeStr = nextAvailableAtDate && !Number.isNaN(nextAvailableAtDate.getTime())
    ? nextAvailableAtDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
    })
    : null
  const scheduleSecondary = joinOfferingMetaDetails([scheduleTimeStr, durationLabel])
  const locationSecondary = joinOfferingMetaDetails([distanceLabel, coachFacilityFullAddress])
  const showScheduleMetaRow = Boolean(availabilityWindowLabel || scheduleDatePrimary || durationLabel)
  const actionLabel = ctaLabel || 'Book Now'
  const showLessonSuffix = !priceLabel && offering.priceInCents != null

  const handleDialogClose = (event) => {
    event?.stopPropagation?.()
    onClose()
  }

  const handleBookNowClick = (event) => {
    event.stopPropagation()
    onPrimaryActionClick?.({
      dialog_name: 'private_offering_dialog',
      button_name: actionLabel,
    })
    onClose()
  }

  const imageUrl = PGA_COACH_PRIVATE_LISTING_IMAGE
  const imageUrlOptimized = imageUrl ? composeOptimizedImageUrl(imageUrl, 1200) : null

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.modalPaper }}
    >
      <DialogContent className={classes.modalContent}>
        <div className={classes.modalScrollArea}>
          <div className={classes.modalHeaderStickyActions}>
            {typeof onToggleOfferingFavorite === 'function' && (
              <Tooltip title={isOfferingFavorite ? 'Remove from saved offerings' : 'Save offering'}>
                <IconButton
                  size="small"
                  aria-label={isOfferingFavorite ? 'Remove offering from saved list' : 'Save offering to your list'}
                  aria-pressed={Boolean(isOfferingFavorite)}
                  className={classes.modalHeaderActionButton}
                  onClick={(event) => {
                    event.stopPropagation()
                    onToggleOfferingFavorite()
                  }}
                >
                  {isOfferingFavorite
                    ? <FavoriteIcon className={classes.modalFavoriteIconSaved} fontSize="small" />
                    : <FavoriteBorderIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              className={classes.modalHeaderActionButton}
              aria-label="Close private offering details"
              onClick={handleDialogClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.modalHeaderImageWrap}>
            <img
              src={imageUrlOptimized}
              alt={offering.title || 'Private offering image'}
              className={classes.modalHeaderImage}
            />
          </div>

          <div className={classes.modalBody}>
            <Box className={classes.modalTopChips}>
              <Chip label="Private" size="small" className={classes.modalChip} />
              {getTagLabels(offering).map((label) => (
                <Chip key={label} label={label} size="small" className={classes.modalChip} />
              ))}
            </Box>
            <Typography variant="h4" className={classes.modalTitle}>
              {offering.title}
            </Typography>

            {offering.description && (
              <div className={classes.modalDescriptionWrap}>
                <div
                  ref={descRef}
                  className={`${classes.modalDescription} ${!descExpanded ? classes.modalDescriptionClamped : ''}`}
                >
                  <MarkdownText paragraphVariant="body1">
                    {offering.description}
                  </MarkdownText>
                </div>
                {descOverflows && (
                  <MuiLink
                    component="button"
                    type="button"
                    underline="hover"
                    className={classes.modalCoachToggle}
                    onClick={() => setDescExpanded((prev) => !prev)}
                  >
                    {descExpanded ? 'Show less' : 'More info'}
                  </MuiLink>
                )}
              </div>
            )}

            <div className={classes.modalCoachRow}>
              <Avatar
                className={classes.modalCoachAvatar}
                src={offering.coach.photoUrl || defaults.coachPlaceholder}
                alt={offering.coach.name || 'Coach'}
              />
              <div className={classes.modalCoachInfo}>
                {offering.coach.slug ? (
                  <MuiLink
                    href={`/coach/${offering.coach.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    className={classes.modalCoachNameLink}
                  >
                    {offering.coach.name || 'PGA Coach'}
                  </MuiLink>
                ) : (
                  <Typography className={classes.modalCoachName}>
                    {offering.coach.name || 'PGA Coach'}
                  </Typography>
                )}
                {(offering.coach.bio || offering.coach.credentials) && (
                  <>
                    <div
                      ref={bioRef}
                      className={`${classes.modalCoachText} ${!bioExpanded ? classes.modalCoachTextClamped : ''}`}
                    >
                      {offering.coach.bio ? (
                        <MarkdownText paragraphVariant="body1">
                          {offering.coach.bio}
                        </MarkdownText>
                      ) : (
                        toTitleFormat(offering.coach.credentials)
                      )}
                    </div>
                    {bioOverflows && (
                      <MuiLink
                        component="button"
                        type="button"
                        underline="hover"
                        className={classes.modalCoachToggle}
                        onClick={() => setBioExpanded((prev) => !prev)}
                      >
                        {bioExpanded ? 'Show less' : 'More info'}
                      </MuiLink>
                    )}
                  </>
                )}
              </div>
            </div>

            {(venueName || coachFacilityFullAddress || distanceLabel) && (
              <div className={classes.modalMetaRow}>
                <MapPin {...COACH_PROFILE_DIALOG_META_ICON_PROPS} className={classes.modalMetaIcon} />
                <div className={classes.modalMetaTextBlock}>
                  <Typography component="p" className={classes.modalMetaTitle}>
                    {formatLocation(offering)}
                  </Typography>
                  {locationSecondary ? (
                    <Typography component="p" className={classes.modalMetaSub}>
                      {locationSecondary}
                    </Typography>
                  ) : null}
                </div>
              </div>
            )}

            {showScheduleMetaRow && (
              <div className={classes.modalMetaRow}>
                {(availabilityWindowLabel || scheduleDatePrimary) ? (
                  <Calendar {...COACH_PROFILE_DIALOG_META_ICON_PROPS} className={classes.modalMetaIcon} />
                ) : (
                  <Clock {...COACH_PROFILE_DIALOG_META_ICON_PROPS} className={classes.modalMetaIcon} />
                )}
                <div className={classes.modalMetaTextBlock}>
                  {availabilityWindowLabel && (
                    <Typography component="p" className={classes.modalMetaTitle}>
                      {availabilityWindowLabel}
                    </Typography>
                  )}
                  {!availabilityWindowLabel && scheduleDatePrimary && (
                    <Typography component="p" className={classes.modalMetaTitle}>
                      {scheduleDatePrimary}
                    </Typography>
                  )}
                  {!availabilityWindowLabel && !scheduleDatePrimary && durationLabel && (
                    <Typography component="p" className={classes.modalMetaTitle}>
                      {durationLabel}
                    </Typography>
                  )}
                  {availabilityWindowLabel && durationLabel && (
                    <Typography component="p" className={classes.modalMetaSub}>
                      {formatLessonDurationLabel(durationInMinutes)}
                    </Typography>
                  )}
                  {!availabilityWindowLabel && scheduleSecondary && scheduleDatePrimary && (
                    <Typography component="p" className={classes.modalMetaSub}>
                      {scheduleSecondary}
                    </Typography>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>

        <div className={classes.modalFooter}>
          <div className={classes.modalFooterPriceArea}>
            <Typography variant="h4" className={classes.modalPrice} component="span">
              {formatPrice(offering)}
            </Typography>
            {showLessonSuffix && (
              <Typography className={classes.modalPriceSuffix} component="span">
                /lesson
              </Typography>
            )}
          </div>

          <div>
            {ctaUrl && (
              <ButtonCta
                externalLink={ctaUrl}
                color="primary"
                variant="contained"
                className={classes.modalBookButton}
                onClick={handleBookNowClick}
              >
                {actionLabel}
              </ButtonCta>
            )}

            {!ctaUrl && (
              <Button
                color="primary"
                variant="contained"
                className={classes.modalBookButton}
                onClick={handleDialogClose}
              >
                Close
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

PrivateOfferingDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  offering: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    format: PropTypes.string,
    ctaUrl: PropTypes.string,
    ctaLabel: PropTypes.string,
    imageUrl: PropTypes.string,
    venueName: PropTypes.string,
    nextAvailableAt: PropTypes.instanceOf(Date),
    coachFacilityFullAddress: PropTypes.string,
    geoDistanceMeters: PropTypes.number,
    durationInMinutes: PropTypes.number,
    priceLabel: PropTypes.string,
    priceInCents: PropTypes.number,
    coach: PropTypes.shape({
      name: PropTypes.string,
      credentials: PropTypes.string,
      bio: PropTypes.string,
      photoUrl: PropTypes.string,
      slug: PropTypes.string,
    }),
  }).isRequired,
  formatPrice: PropTypes.func.isRequired,
  formatLocation: PropTypes.func.isRequired,
  getTagLabels: PropTypes.func.isRequired,
  onPrimaryActionClick: PropTypes.func,
  availabilityWindowLabel: PropTypes.string,
  isOfferingFavorite: PropTypes.bool,
  onToggleOfferingFavorite: PropTypes.func,
}

PrivateOfferingDialog.defaultProps = {
  open: false,
  onPrimaryActionClick: undefined,
  availabilityWindowLabel: undefined,
  isOfferingFavorite: false,
  onToggleOfferingFavorite: undefined,
}

export default PrivateOfferingDialog
